/**
 * notification-grouping — date-section helpers for the bell tray (Wave 3.I).
 *
 * Mirrors legacy `Notifications.util.js#getNotificationDate` but extends
 * the "within last 7 days" window to use weekday names (e.g. "Mon, Apr 28")
 * per Wave 3.I spec, falling through to a full date string for older
 * items.
 *
 * Pure functions only — extracted out of `NotificationsBell.tsx` so the
 * grouping logic is testable without rendering the component.
 */

import type { Notification } from '@/api/types-bridge'
import { i18n } from '@/i18n'

function weekdayFormatter(locale: string) {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}
function fullDateFormatter(locale: string) {
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}
function timeFormatter(locale: string) {
  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** Strip a Date down to local-midnight for day-boundary comparisons. */
export function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

/**
 * Section label for a notification's createdAt. Produces one of:
 *   - "Today"
 *   - "Yesterday"
 *   - "Mon, Apr 28" (within the last 7 days)
 *   - "Apr 21, 2026" (older)
 *
 * Returns "Earlier" for invalid timestamps so the row still groups
 * sensibly under a labeled section instead of a blank header.
 */
export function sectionLabelFor(iso: string, now: Date, locale: string): string {
  const then = new Date(iso)
  if (Number.isNaN(then.getTime())) return i18n.t('groups.earlier', { ns: 'notifications' })
  const today = startOfLocalDay(now)
  const target = startOfLocalDay(then)
  const dayMs = 24 * 60 * 60 * 1000
  const diffDays = Math.round((today.getTime() - target.getTime()) / dayMs)
  if (diffDays <= 0) return i18n.t('groups.today', { ns: 'notifications' })
  if (diffDays === 1) return i18n.t('groups.yesterday', { ns: 'notifications' })
  if (diffDays < 7) return weekdayFormatter(locale).format(then)
  return fullDateFormatter(locale).format(then)
}

/**
 * Compact "time of day" used as the row's right-side timestamp. Today
 * rows show `3:42 PM`; yesterday → "Yesterday"; last-7-days → weekday;
 * older → full date. Falls back to empty for invalid input.
 */
export function rowTimestamp(iso: string, now: Date, locale: string): string {
  const then = new Date(iso)
  if (Number.isNaN(then.getTime())) return ''
  const today = startOfLocalDay(now)
  const target = startOfLocalDay(then)
  const dayMs = 24 * 60 * 60 * 1000
  const diffDays = Math.round((today.getTime() - target.getTime()) / dayMs)
  if (diffDays <= 0) return timeFormatter(locale).format(then)
  if (diffDays === 1) return i18n.t('groups.yesterday', { ns: 'notifications' })
  if (diffDays < 7) return weekdayFormatter(locale).format(then)
  return fullDateFormatter(locale).format(then)
}

export interface NotificationGroup {
  label: string
  items: Notification[]
}

/**
 * Group notifications by their section label, preserving the input
 * order. The BE returns items DESC by createdAt, so the resulting groups
 * naturally come out as Today → Yesterday → ... → Older.
 */
export function groupNotifications(
  items: Notification[],
  now: Date,
  locale: string,
): NotificationGroup[] {
  const out: NotificationGroup[] = []
  let current: NotificationGroup | null = null
  for (const n of items) {
    const label = sectionLabelFor(n.createdAt, now, locale)
    if (!current || current.label !== label) {
      current = { label, items: [] }
      out.push(current)
    }
    current.items.push(n)
  }
  return out
}
